import type { AmpLibraryFileFragment } from "src/graphql";
import {
  downloadBlob,
  downloadFromUrl,
  sanitizeFilename,
} from "src/utils/download";

type FileDownload =
  // An upload's presigned URL serves `Content-Disposition: attachment`, so a plain link click saves it.
  | { attachmentUrl: string; filename: string }
  | { text: string; type: string; filename: string };

// A stored URL that is not http(s) must never reach a link click.
function isHttpUrl(url: string) {
  return (
    URL.canParse(url) && ["https:", "http:"].includes(new URL(url).protocol)
  );
}

/** Whether a file downloads through a format choice rather than as its one stored file. */
export function hasDownloadFormats(file: AmpLibraryFileFragment) {
  return Boolean(file.ad || file.email?.html || file.html || file.markdown);
}

export function canDownloadFile(file: AmpLibraryFileFragment) {
  return hasDownloadFormats(file) || getFileDownload(file) !== null;
}

/** What a file downloads as without a format choice; ads, emails, web pages and docs choose one in `DownloadFileDialog`, and a doc's Markdown option and bulk download use its entry here. */
export function getFileDownload(
  file: AmpLibraryFileFragment,
): FileDownload | null {
  const name = sanitizeFilename(file.name ?? "") || "Untitled";
  if (
    file.slides?.downloadUrl &&
    file.slides.filename &&
    isHttpUrl(file.slides.downloadUrl)
  )
    return {
      attachmentUrl: file.slides.downloadUrl,
      filename: file.slides.filename,
    };
  if (file.upload && isHttpUrl(file.upload.downloadUrl))
    return {
      attachmentUrl: file.upload.downloadUrl,
      filename: file.upload.filename,
    };
  if (file.markdown)
    return {
      text: file.markdown.markdown,
      type: "text/markdown",
      filename: `${name}.md`,
    };
  const messageText = [file.message?.title, file.message?.body]
    .filter(Boolean)
    .join("\n\n");
  if (messageText)
    return { text: messageText, type: "text/plain", filename: `${name}.txt` };
  return null;
}

export function downloadFile(download: FileDownload) {
  if ("text" in download) {
    downloadBlob(
      new Blob([download.text], { type: download.type }),
      download.filename,
    );
    return;
  }
  downloadFromUrl(download.attachmentUrl, download.filename);
}
